/** Persist delimiter between free-text description and HSIC selection. */
export const BUSINESS_NATURE_SEP = '||';

/** HSIC when Principal activities = Inactive / dormant. */
export const INACTIVE_HSIC_CODE = '000000';

/** Dropdown label: code | nameZh */
export function toHsicOptionLabel(code: string, nameZh: string): string {
    return `${code} | ${nameZh}`;
}

/** Persist label: code - nameZh */
export function toHsicSaveText(code: string, nameZh: string): string {
    return `${code} - ${nameZh}`;
}

/** True when persisted HSIC segment is (or starts with) inactive code 000000. */
export function isInactiveHsicSaved(hsicSaved: string | null | undefined): boolean {
    const t = String(hsicSaved || '').trim();
    return t === INACTIVE_HSIC_CODE || /^000000\b/.test(t);
}

/** Map stored HSIC segment back to the Autocomplete option label when possible. */
export function hsicSavedToOptionLabel(saved: string): string {
    const matched = saved.match(/^(\d{6})\s*-\s*(.+)$/);
    return matched ? toHsicOptionLabel(matched[1], matched[2].trim()) : saved;
}

/** Map a selected option label (or free-typed code) back to the persisted HSIC segment. */
export function hsicOptionLabelToSaved(optionLabel: string): string {
    const trimmed = optionLabel.trim();
    const pipe = trimmed.match(/^(\d{6})\s*\|\s*(.+)$/);
    if (pipe) return toHsicSaveText(pipe[1], pipe[2].trim());
    const dash = trimmed.match(/^(\d{6})\s*-\s*(.*)$/);
    if (dash) {
        const name = dash[2].trim();
        return name ? toHsicSaveText(dash[1], name) : dash[1];
    }
    const codeOnly = trimmed.match(/^(\d{6})$/);
    if (codeOnly) return codeOnly[1];
    return trimmed;
}

export type BusinessNatureParts = {
    freeText: string;
    hsicSaved: string;
};

/**
 * Split stored businessNature into free-text + HSIC segments.
 * Formats: `text || hsic`, `||hsic`, `text||`, or legacy single segment.
 */
export function splitBusinessNature(saved: string | null | undefined): BusinessNatureParts {
    if (saved == null || saved === '') {
        return {freeText: '', hsicSaved: ''};
    }
    const sepIdx = saved.indexOf(BUSINESS_NATURE_SEP);
    if (sepIdx >= 0) {
        return {
            freeText: saved.slice(0, sepIdx).trim(),
            hsicSaved: saved.slice(sepIdx + BUSINESS_NATURE_SEP.length).trim(),
        };
    }
    const trimmed = saved.trim();
    if (/^\d{6}\s*-\s*.+$/.test(trimmed)) {
        return {freeText: '', hsicSaved: trimmed};
    }
    return {freeText: saved, hsicSaved: ''};
}

/**
 * Join free-text + HSIC for persistence.
 * Both ? `text || hsic`; HSIC only ? `||hsic`; free only ? `text||`.
 */
export function joinBusinessNature(freeText: string, hsicSaved: string): string | null {
    const free = freeText.trim();
    const hsic = hsicSaved.trim();
    if (!free && !hsic) return null;
    if (free && hsic) return `${free} ${BUSINESS_NATURE_SEP} ${hsic}`;
    if (!free && hsic) return `${BUSINESS_NATURE_SEP}${hsic}`;
    return `${free}${BUSINESS_NATURE_SEP}`;
}
